import { get, set } from 'idb-keyval';

// Submissions made while the phone had no signal wait here until it is back.
//
// Every queued request carries a client-generated idempotency key, so a replay
// that the server already processed returns the original response instead of
// creating a second purchase or sale.

const KEY = 'diesel-outbox-v1';
const listeners = new Set();

export const newKey = () =>
  (globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`);

export async function readOutbox() {
  return (await get(KEY)) || [];
}

async function writeOutbox(items) {
  await set(KEY, items);
  listeners.forEach((fn) => fn(items));
}

export function subscribeOutbox(fn) {
  listeners.add(fn);
  readOutbox().then(fn);
  return () => listeners.delete(fn);
}

export async function enqueue(item) {
  const items = await readOutbox();
  const entry = { ...item, id: newKey(), queued_at: new Date().toISOString(), attempts: 0, error: null };
  await writeOutbox([...items, entry]);
  return entry;
}

export async function removeFromOutbox(id) {
  const items = await readOutbox();
  await writeOutbox(items.filter((i) => i.id !== id));
}

async function markFailed(id, message) {
  const items = await readOutbox();
  await writeOutbox(items.map((i) => (i.id === id ? { ...i, attempts: i.attempts + 1, error: message } : i)));
}

/**
 * Replays the queue oldest-first. Stops at the first network failure so items
 * stay in order; a request the server rejects on its merits (a 4xx) is left in
 * the queue flagged with the reason, because silently dropping a sale would be
 * worse than making someone look at it.
 */
export async function flushOutbox(send) {
  const items = await readOutbox();
  const result = { sent: 0, failed: 0, remaining: items.length };

  for (const item of items) {
    try {
      await send(item);
      await removeFromOutbox(item.id);
      result.sent += 1;
      result.remaining -= 1;
    } catch (err) {
      if (err.isNetworkError) break; // still offline - try again later
      await markFailed(item.id, err.message);
      result.failed += 1;
    }
  }
  return result;
}
